import * as React from 'react';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { faMemoCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import Icon from '../../atoms/Icon';
import Button, { ButtonProps } from '../../atoms/Button';
import { useModal } from '../Modal';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  modalClassName?: string;
  modalStyle?: React.CSSProperties;
  as?: React.ElementType;
  variant?: ButtonProps['variant'];
  type?: ButtonProps['type'];
  size?: ButtonProps['size'];
  icon?: FontAwesomeIconProps['icon'] | null;
  modalId?: string;
  modalTitle?: string;
  label?: string;
  children: React.ReactNode;
  onShow?: () => void;
  onHide?: () => void;
  disabled?: boolean;
  isModalScrollable?: boolean;
}

export const ModalTrigger: React.FunctionComponent<Props> = ({
  className,
  style,
  modalClassName,
  modalStyle,
  as: Component = Button,
  icon = faMemoCircleInfo,
  variant,
  type,
  size,
  modalId = 'triggered-modal',
  modalTitle,
  label,
  children,
  onShow,
  onHide,
  disabled = false,
  isModalScrollable,
  ...htmlElementProps
}) => {
  const { createModal, destroyModal } = useModal();

  const handleOnHide = React.useCallback(() => {
    destroyModal(modalId);
    onHide?.();
  }, [destroyModal, modalId, onHide]);

  const handleOnShow = React.useCallback(() => {
    createModal({
      id: modalId,
      className: modalClassName,
      title: modalTitle,
      onHide: () => handleOnHide(),
      style: modalStyle,
      isScrollable: isModalScrollable,
      children,
      ...htmlElementProps,
    });
    onShow?.();
  }, [
    children,
    createModal,
    handleOnHide,
    htmlElementProps,
    isModalScrollable,
    modalClassName,
    modalId,
    modalStyle,
    modalTitle,
    onShow,
  ]);

  return (
    <Component
      className={className}
      style={style}
      variant={variant}
      size={size}
      type={type}
      onClick={() => handleOnShow()}
      disabled={disabled}
    >
      {icon && <Icon className={label ? 'mr-2' : undefined} icon={icon} />}
      {label}
    </Component>
  );
};

export default ModalTrigger;
